import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { getDashboardGarEmpresa } from '../../../../../app/modules/helpers/Api';
import { faixaDeRisco, Faixa } from '../charts/faixaDeRisco';
import { toolboxDownload } from '../charts/toolboxDownload';
import { eixoCategoria, eixoSeveridade } from '../charts/eixosBase';
import GriMatrizChart, { MatrizCelula } from '../ManagementRiskAnalysis/GriMatrizChart';

interface LinhaSeveridade {
    tema?: string;
    modelo?: string;
    modelo_id?: number;
    severidade_inicial: number;
    severidade_residual: number;
    riscos: number;
}

interface DadosGar {
    empresa: {
        id: number;
        modelos_com_coleta: number;
        links_finalizados: number;
        riscos_avaliados: number;
    };
    por_tema: LinhaSeveridade[];
    por_modelo: LinhaSeveridade[];
    matriz: {
        prob: number;
        impacto: number;
        nivel: number;
        faixa: Faixa;
        total: number;
        titulos_distintos: number;
        riscos: { titulo: string; tema: string }[];
    }[];
}

interface Props {
    empresaId: number;
}

const PainelGar: React.FC<Props> = ({ empresaId }) => {
    const [dados, setDados] = useState<DadosGar | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(false);

    const refTema = React.useRef<any>(null);
    const refModelo = React.useRef<any>(null);

    useEffect(() => {
        setErro(false);
        setCarregando(true);

        getDashboardGarEmpresa(empresaId)
            .then((response) => setDados(response.data.data))
            .catch(() => setErro(true))
            .finally(() => setCarregando(false));
    }, [empresaId]);

    const barras = (linhas: LinhaSeveridade[]) => {
        const iniciais = linhas.map((linha) => {
            const cor = faixaDeRisco(linha.severidade_inicial);
            return {
                value: linha.severidade_inicial,
                itemStyle: { color: cor.fill, borderColor: cor.border, borderWidth: 1 },
            };
        });

        const residuais = linhas.map((linha) => {
            const cor = faixaDeRisco(linha.severidade_residual);
            return {
                value: linha.severidade_residual,
                itemStyle: {
                    color: cor.fill,
                    borderColor: cor.border,
                    borderWidth: 1,
                    borderType: 'dashed' as 'dashed',
                    opacity: 0.7,
                },
            };
        });

        return [
            { name: 'Antes do plano', type: 'bar', data: iniciais, barWidth: '30%' },
            { name: 'Após o plano', type: 'bar', data: residuais, barWidth: '30%' },
        ];
    };

    const opcao = (
        linhas: LinhaSeveridade[],
        campo: 'tema' | 'modelo',
        titulo: string,
        nomeEixoX: string,
        nomeArquivo: string,
        ref: React.RefObject<any>
    ) => ({
        title: {
            text: titulo,
            left: 'center',
            textStyle: { color: '#333', fontSize: 16 },
        },
        legend: { bottom: 0 },
        grid: { top: 60, bottom: 70 },
        tooltip: {
            trigger: 'axis',
            formatter: (params: any[]) => {
                const linha = linhas[params[0].dataIndex];
                const cabecalho = `${linha[campo]} — ${linha.riscos} risco(s)`;
                const series = params.map((p) => `${p.seriesName}: ${p.value}`);

                return [cabecalho, ...series].join('<br/>');
            },
        },
        xAxis: eixoCategoria(nomeEixoX, linhas.map((linha) => String(linha[campo]))),
        yAxis: eixoSeveridade('Média de Severidade'),
        series: barras(linhas),
        toolbox: toolboxDownload(ref, nomeArquivo),
    });

    const celulas = (): MatrizCelula[] => {
        if (!dados) {
            return [];
        }

        return dados.matriz.map((celula) => ({
            prob: celula.prob,
            impacto: celula.impacto,
            rotulo: String(celula.total),
            faixa: celula.faixa,
            riscos: celula.riscos,
            total: celula.titulos_distintos,
        }));
    };

    if (carregando) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                <span className='spinner-border spinner-border-lg align-middle'></span>
            </div>
        );
    }

    if (erro) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                <span className='text-gray-600 fw-bold fs-4'>Não foi possível carregar a análise de riscos.</span>
            </div>
        );
    }

    const temDados = dados !== null && (
        dados.por_tema.length > 0 || dados.por_modelo.length > 0 || dados.matriz.length > 0
    );

    if (!temDados || !dados) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                <span className='text-gray-600 fw-bold fs-4'>Esta empresa ainda não tem coleta de risco finalizada.</span>
            </div>
        );
    }

    return (
        <div className='col-12 mb-10'>
            <h3 className='mb-1'>Análise de riscos</h3>
            <p className='text-gray-600 fw-bold fs-7'>
                {dados.empresa.riscos_avaliados} avaliações em {dados.empresa.links_finalizados} links
                de coleta, sobre {dados.empresa.modelos_com_coleta} modelo(s)
            </p>

            {dados.por_tema.length > 0 && (
                <ReactECharts
                    ref={refTema}
                    option={opcao(dados.por_tema, 'tema', 'Severidade média por tema', 'Temas de Risco', 'gar-empresa-por-tema', refTema)}
                    opts={{ renderer: 'svg' }}
                    style={{ minHeight: '380px', width: '100%' }}
                />
            )}

            {dados.por_modelo.length > 0 && (
                <ReactECharts
                    ref={refModelo}
                    option={opcao(dados.por_modelo, 'modelo', 'Severidade média por modelo', 'Modelos de GAR', 'gar-empresa-por-modelo', refModelo)}
                    opts={{ renderer: 'svg' }}
                    style={{ minHeight: '380px', width: '100%' }}
                />
            )}

            {dados.matriz.length > 0 && (
                <GriMatrizChart celulas={celulas()} titulo='Matriz de Risco da Empresa' mostrarTotalNoTooltip />
            )}
        </div>
    );
};

export default PainelGar;
